import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Edit2, Eye, Save, Building2, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { AIMemorySection } from "@/components/ai-memory-section";
import { AiMemoryEntriesPanel } from "@/components/ai-memory-entries-panel";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const DEFAULT_ACCOUNT_KB_TEMPLATE = `# Company Policies
[Add your general company policies here]

# Standard House Rules
[Add house rules that apply across all your properties]

# Standard Responses
[Add standard responses you send to members]

# Escalation Procedures
[Add escalation procedures for common issues]

# Emergency Contacts
[Add general emergency contacts]

# Notes
[Add any other account-wide information]
`;

export default function AppAccountKnowledgeBase() {
  const queryClient = useQueryClient();
  usePageTitle("Knowledge Base");
  const { toast } = useToast();
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const kbQuery = useQuery<{ content: string | null; isOwner: boolean }>({
    queryKey: ["/api/app/account/knowledge-base"],
    queryFn: async () => {
      const res = await fetch("/api/app/account/knowledge-base", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  const propertiesQuery = useQuery<Array<{ id: number; name: string; address?: string | null }>>({
    queryKey: ["/api/app/properties"],
  });

  const aiDraftingAccessQuery = useQuery<{ allowed: boolean }>({
    queryKey: ["/api/app/ai-drafting/access"],
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  // Fail CLOSED: hidden by default unless the server confirms access.
  const aiDraftingAllowed = aiDraftingAccessQuery.data?.allowed === true;

  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await fetch("/api/app/account/knowledge-base", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/account/knowledge-base"] });
      setEditMode(false);
      setDraft(null);
      toast({ title: "Account knowledge base saved" });
    },
    onError: () => {
      toast({ title: "Failed to save knowledge base", variant: "destructive" });
    },
  });

  const isOwner = kbQuery.data?.isOwner ?? false;
  const canEdit = isOwner;

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto p-6">
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BookOpen className="h-5 w-5 text-brand" />
                <h1
                  className="text-xl font-semibold text-gray-900"
                  data-testid="text-account-kb-heading"
                  data-tour="account-knowledge-base-heading"
                >
                  Account Knowledge Base
                </h1>
              </div>
              <p className="text-sm text-gray-500">
                Global information that applies across all your properties — company policies, standard house rules, escalation procedures, and more. AI features use this alongside each property&apos;s own knowledge base.
              </p>
              {!kbQuery.isLoading && !canEdit && (
                <p className="text-xs text-gray-500 mt-2" data-testid="text-account-kb-readonly-notice">
                  Read-only — only the account owner can edit this knowledge base.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="account-knowledge-base" />
              <GuideButton pageKey="account-knowledge-base" />
            </div>
          </div>
        </div>

        <GuideNudgeBanner pageKey="account-knowledge-base" />

        <div
          className="mb-6 mt-6 border border-blue-200 bg-blue-50 rounded-lg p-4"
          data-testid="callout-property-kb-pointer"
          data-tour="account-knowledge-base-property-pointer"
        >
          <div className="flex items-start gap-3">
            <Building2 className="h-5 w-5 text-brand shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                Looking for a property-specific knowledge base?
              </h3>
              <p className="text-sm text-gray-700 mb-3">
                This page holds info that applies to <strong>every</strong> property. Each
                property also has its own knowledge base for details that only apply to that
                house — like the WiFi password, lockbox codes, parking instructions, or
                quirks of the building. AI features combine both when answering questions.
              </p>
              <p className="text-xs text-gray-600 mb-3">
                To open a property&apos;s knowledge base: go to{" "}
                <Link
                  href="/app/properties"
                  className="text-brand underline font-medium"
                  data-testid="link-properties-list"
                >
                  Properties
                </Link>
                , click a property, then select the <strong>Knowledge Base</strong> tab.
              </p>
              {propertiesQuery.isLoading ? (
                <Skeleton className="h-8 w-48" />
              ) : propertiesQuery.data && propertiesQuery.data.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {propertiesQuery.data.slice(0, 6).map((p) => (
                    <Link
                      key={p.id}
                      href={`/app/properties/${p.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-blue-200 text-brand hover:bg-blue-100 transition-colors"
                      data-testid={`link-property-kb-${p.id}`}
                    >
                      {p.name}
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  ))}
                  {propertiesQuery.data.length > 6 && (
                    <Link
                      href="/app/properties"
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-blue-200 text-brand hover:bg-blue-100 transition-colors"
                      data-testid="link-property-kb-more"
                    >
                      +{propertiesQuery.data.length - 6} more
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              ) : (
                <Link
                  href="/app/properties"
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-white border border-blue-200 text-brand hover:bg-blue-100 transition-colors"
                  data-testid="link-property-kb-empty"
                >
                  Add your first property
                  <ArrowRight className="h-3 w-3" />
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-6" data-tour="account-knowledge-base-content">
          <div className="flex items-center justify-between mb-6">
            <span className="text-xs text-gray-500">Account-wide reference information</span>
            <div className="flex items-center gap-2">
              {!canEdit ? null : !editMode ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const current = kbQuery.data?.content || DEFAULT_ACCOUNT_KB_TEMPLATE;
                    setDraft(current);
                    setEditMode(true);
                  }}
                  data-testid="button-account-kb-edit"
                >
                  <Edit2 className="h-4 w-4 mr-1" /> Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditMode(false);
                      setDraft(null);
                    }}
                    data-testid="button-account-kb-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-brand"
                    disabled={saveMutation.isPending}
                    onClick={() => {
                      if (draft !== null) saveMutation.mutate(draft);
                    }}
                    data-testid="button-account-kb-save"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saveMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
            </div>
          </div>

          {kbQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : editMode ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="account-kb-editor-area">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-2">
                  <Edit2 className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500 font-medium">Editor</span>
                </div>
                <Textarea
                  value={draft ?? ""}
                  onChange={(e) => setDraft(e.target.value)}
                  rows={30}
                  className="font-mono text-sm resize-none flex-1 min-h-[500px]"
                  placeholder="Write your account-wide knowledge base in Markdown..."
                  data-testid="textarea-account-kb-editor"
                />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-2">
                  <Eye className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500 font-medium">Preview</span>
                </div>
                <div className="border rounded-md p-4 bg-gray-50 overflow-auto min-h-[500px] prose prose-sm max-w-none" data-testid="account-kb-preview-area">
                  <ReactMarkdown>{draft ?? ""}</ReactMarkdown>
                </div>
              </div>
            </div>
          ) : (
            <div data-testid="account-kb-content-area">
              {kbQuery.data?.content ? (
                <div className="prose prose-sm max-w-none border rounded-md p-6 bg-gray-50 min-h-[300px]" data-testid="account-kb-rendered-content">
                  <ReactMarkdown>{kbQuery.data.content}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-lg" data-testid="account-kb-empty-state">
                  <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-base font-medium text-gray-600 mb-1">No account knowledge base yet</h4>
                  <p className="text-sm text-gray-400 mb-4">
                    {canEdit
                      ? "Add company-wide policies, standard house rules, and escalation procedures that apply to all your properties."
                      : "The account owner hasn't added any account-wide knowledge base content yet."}
                  </p>
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setDraft(DEFAULT_ACCOUNT_KB_TEMPLATE);
                        setEditMode(true);
                      }}
                      data-testid="button-account-kb-use-template"
                    >
                      <Edit2 className="h-4 w-4 mr-1" /> Use Template
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <AIMemorySection
            endpoint="/api/app/account/ai-memory"
            queryKey={["/api/app/account/ai-memory"]}
            canEdit={canEdit}
            description="PadPulse remembers facts about your business here (e.g. preferred vendors, recurring policies). You can edit them anytime."
            testIdPrefix="account-ai-memory"
            aiDraftingAllowed={aiDraftingAllowed}
          />
          <AiMemoryEntriesPanel
            listQueryKey={["/api/app/account/ai-memory-entries"]}
            listEndpoint="/api/app/account/ai-memory-entries"
            canEdit={canEdit}
            testIdPrefix="account-ai-memory-entries"
            aiDraftingAllowed={aiDraftingAllowed}
          />
        </div>
      </div>
    </AppLayout>
  );
}
